import { useState } from 'react';
import { ChevronDown, ChevronRight, FileText, AlertTriangle } from 'lucide-react';
import { formatDateTime, type StageReportResult } from '@ai-tiaozs/shared';

function Collapse({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="card">
      <button
        type="button"
        className="flex w-full items-center justify-between text-left text-sm font-semibold text-slate-700"
        onClick={() => setOpen((v) => !v)}
      >
        <span>{title}</span>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </section>
  );
}

export function StageReportDetail({ report }: { report: StageReportResult }) {
  const rangeLabel =
    report.rangeStart === report.rangeEnd
      ? `第 ${report.rangeStart} 次记录`
      : `第 ${report.rangeStart} - ${report.rangeEnd} 次`;

  const dq = report.dataQuality;
  const timeSpan =
    report.createdAt != null
      ? formatDateTime(report.createdAt)
      : '';

  return (
    <div className="space-y-3">
      <section className="card border-teamB-primary/30 bg-teamB-primary/5">
        <h2 className="flex items-center gap-1 text-sm font-semibold text-teamB-primary">
          <FileText className="h-4 w-4" /> {report.plantName} · 共 {report.observationCount} 条记录
          {rangeLabel ? ` · ${rangeLabel}` : ''}
        </h2>
        {timeSpan && <p className="mt-0.5 text-[11px] text-slate-500">生成于 {timeSpan}</p>}
        {report.duplicate && (
          <p className="mt-1 text-xs text-teamA-warning">此为已有报告(记录集未变化)</p>
        )}
      </section>

      {(dq?.isMixedArchive || (dq?.mismatchCount ?? 0) > 0) && (
        <section className="card border-teamA-warning/40 bg-teamA-warning/5">
          <h2 className="flex items-center gap-1 text-sm font-semibold text-teamA-warning">
            <AlertTriangle className="h-4 w-4" /> 数据说明
          </h2>
          <p className="mt-1 text-sm text-slate-700">
            {dq.dataQualityNote ??
              `档案种类为「${dq.archiveSpecies}」,${dq.mismatchCount} 条记录照片疑似其他植物。`}
          </p>
          {dq.mismatchRecords.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-slate-600">
              {dq.mismatchRecords.map((m) => (
                <li key={`${m.recordIndex}-${m.createdAt}`}>
                  第 {m.recordIndex} 次 · {formatDateTime(m.createdAt)} · 识别为「{m.detectedSpecies}」
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-[11px] text-slate-500">
            可靠记录 {dq.reliableRecordCount} 条,以下总结已区分档案植物与错挂条目。
          </p>
        </section>
      )}

      <section className="card">
        <h2 className="text-sm font-semibold text-slate-700">📝 AI 总结</h2>
        <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">
          {report.summary}
        </p>
      </section>

      <Collapse title="📊 家庭养护统计">
        <div className="space-y-1 text-sm text-slate-600">
          <p>浇水 {report.careStats.waterCount} 次</p>
          <p>调整光照 {report.careStats.lightChangeCount} 次</p>
          <p>继续观察 {report.careStats.continueObserveCount} 次</p>
          <p>长新叶 {report.careStats.newLeavesCount} 处</p>
          <p>叶片状态变化 {report.careStats.leafStateChanges.length} 次</p>
          <p>AI 错误案例 {report.mismatchCaseCount} 个</p>
        </div>
      </Collapse>

      <Collapse title={`🌱 养护日记 (${report.growthDiary.length} 条)`}>
        <ol className="space-y-1 text-sm">
          {report.growthDiary.map((line, i) => (
            <li key={i} className="rounded-md bg-slate-50 px-3 py-2 text-slate-700">
              {line}
            </li>
          ))}
        </ol>
      </Collapse>

      {report.careStats.leafStateChanges.length > 0 && (
        <Collapse title={`⚠️ 异常事件 (${report.careStats.leafStateChanges.length})`}>
          <ul className="space-y-1 text-sm">
            {report.careStats.leafStateChanges.map((c, i) => (
              <li key={i} className="rounded-md bg-teamA-warning/10 px-3 py-2 text-teamA-warning">
                {formatDateTime(c.createdAt)}(第 {c.recordIndex} 次):叶片从「{c.from}」变为「{c.to}」
              </li>
            ))}
          </ul>
        </Collapse>
      )}
    </div>
  );
}
